import { useState, useEffect, useCallback } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface SharedItem {
  id: string
  name: string
  type: 'collab' | 'team'
  owner_name?: string
  owner_email?: string
  my_role?: string
  room_id?: string
  team_id?: string
}

export function SharedPanel() {
  const [items, setItems] = useState<SharedItem[]>([])
  const [loading, setLoading] = useState(true)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [tree, setTree] = useState<any[]>([])
  const [treeLoading, setTreeLoading] = useState(false)

  const fetchAll = useCallback(async () => {
    const results: SharedItem[] = []
    try {
      // Fetch collab shared projects
      const collabRes = await authFetch(apiUrl('/api/projects/shared'))
      if (collabRes.ok) {
        const data = await collabRes.json()
        if (Array.isArray(data)) {
          data.forEach((p: any) => results.push({
            id: p.id, name: p.name || p.slug, type: 'collab',
            owner_name: p.owner_name, owner_email: p.owner_email, my_role: p.my_role, room_id: p.room_id,
          }))
        }
      }
    } catch {}
    try {
      // Fetch teams the user belongs to (not owned by them)
      const teamsRes = await authFetch(apiUrl('/api/teams'))
      if (teamsRes.ok) {
        const data = await teamsRes.json()
        const userId = JSON.parse(localStorage.getItem('pulse-auth') ?? '{}')?.state?.user?.id
        if (Array.isArray(data)) {
          data.filter((t: any) => t.owner_id !== userId).forEach((t: any) => results.push({
            id: t.id, name: t.name, type: 'team',
            owner_name: t.owner_name, my_role: t.my_role || 'member', team_id: t.id,
          }))
        }
      }
    } catch {}
    setItems(results)
    setLoading(false)
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  const openSharedItem = async (item: SharedItem) => {
    setActiveId(item.id)
    setTreeLoading(true)
    setTree([])
    try {
      const token = JSON.parse(localStorage.getItem('pulse-auth') ?? '{}')?.state?.token ?? ''
      const headers: Record<string, string> = { 'Authorization': `Bearer ${token}` }
      if (item.type === 'team' && item.team_id) {
        headers['X-Team-Id'] = item.team_id
      } else if (item.type === 'collab' && item.room_id) {
        headers['X-Collab-Room'] = item.room_id
      }
      const res = await fetch(apiUrl('/api/files/list'), { headers })
      if (res.ok) {
        const data = await res.json()
        setTree(Array.isArray(data) ? data : [])
      }
    } catch {}
    setTreeLoading(false)
  }

  const openFile = async (item: SharedItem, filePath: string, fileName: string) => {
    try {
      const token = JSON.parse(localStorage.getItem('pulse-auth') ?? '{}')?.state?.token ?? ''
      const headers: Record<string, string> = { 'Authorization': `Bearer ${token}` }
      if (item.type === 'team' && item.team_id) headers['X-Team-Id'] = item.team_id
      else if (item.type === 'collab' && item.room_id) headers['X-Collab-Room'] = item.room_id
      const res = await fetch(apiUrl(`/api/files/read?path=${encodeURIComponent(filePath)}`), { headers })
      const data = await res.json()
      const { useAppStore } = await import('../../store/appStore')
      useAppStore.getState().openTab({
        id: `shared:${item.id}:${filePath}`,
        path: `/${filePath}`,
        name: fileName,
        language: fileName.split('.').pop() || 'text',
        content: data.content ?? '',
      })
    } catch {}
  }

  const activeItem = items.find(i => i.id === activeId)

  return (
    <div className="sidebar">
      <div className="sidebar__header">
        <span>SHARED WITH YOU</span>
        <button className="sidebar__icon-btn" title="Refresh" onClick={fetchAll}>↻</button>
      </div>

      <div className="sidebar__tree" style={{ padding: '4px 0' }}>
        {loading && <div style={{ padding: 12, opacity: 0.5, fontSize: 10 }}>Loading...</div>}

        {!loading && items.length === 0 && (
          <div style={{ padding: '40px 14px', textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🔗</div>
            <div style={{ color: 'var(--text-2)', fontSize: 11, marginBottom: 6 }}>No shared projects yet</div>
            <div style={{ color: 'var(--text-2)', fontSize: 9, fontFamily: 'var(--font-mono)', opacity: 0.6, lineHeight: 1.6 }}>
              When someone invites you to a team or collab session, their projects will appear here.
            </div>
          </div>
        )}

        {/* List shared items */}
        {!activeId && items.map((item) => (
          <div
            key={item.id}
            onClick={() => openSharedItem(item)}
            className="tree-item"
            style={{ padding: '8px 14px', cursor: 'pointer', gap: 10 }}
          >
            <span style={{ fontSize: 14 }}>{item.type === 'team' ? '👥' : '📁'}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-0)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.name}
              </div>
              <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 1 }}>
                {item.owner_name || item.owner_email || 'Unknown'} · {item.my_role}
              </div>
            </div>
            <span style={{ fontSize: 8, padding: '2px 6px', borderRadius: 4, background: item.type === 'team' ? 'rgba(95,212,164,0.12)' : 'rgba(124,110,245,0.12)', color: item.type === 'team' ? 'var(--accent-2)' : 'var(--accent)', fontWeight: 700, fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
              {item.type}
            </span>
          </div>
        ))}

        {/* Browsing a shared item's files */}
        {activeId && activeItem && (
          <>
            <div style={{ padding: '6px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <button onClick={() => { setActiveId(null); setTree([]) }} style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 11, fontFamily: 'var(--font-mono)' }}>← Back</button>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-0)', fontFamily: 'var(--font-mono)' }}>{activeItem.name}</span>
            </div>
            {treeLoading && <div style={{ padding: 12, opacity: 0.5, fontSize: 10 }}>Loading files...</div>}
            {!treeLoading && tree.length === 0 && <div style={{ padding: 12, opacity: 0.5, fontSize: 10 }}>No files</div>}
            {!treeLoading && tree.map(node => (
              <SharedTreeItem key={node.path} node={node} depth={0} onOpen={(path, name) => openFile(activeItem, path, name)} />
            ))}
          </>
        )}
      </div>
    </div>
  )
}

function SharedTreeItem({ node, depth, onOpen }: { node: any; depth: number; onOpen: (path: string, name: string) => void }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <div
        className={`tree-item ${node.type === 'folder' ? 'tree-item--folder' : ''}`}
        style={{ paddingLeft: 14 + depth * 14, cursor: 'pointer' }}
        onClick={() => { if (node.type === 'folder') setOpen(!open); else onOpen(node.path, node.name) }}
      >
        <span className="tree-item__chevron">{node.type === 'folder' ? (open ? '▾' : '▸') : ''}</span>
        <span className="tree-item__icon">{node.type === 'folder' ? '📁' : '◻'}</span>
        <span className="tree-item__name">{node.name}</span>
      </div>
      {node.type === 'folder' && open && node.children?.map((child: any) => (
        <SharedTreeItem key={child.path} node={child} depth={depth + 1} onOpen={onOpen} />
      ))}
    </>
  )
}
